import { useEffect, useState } from "react";
import api from "@/lib/api";

export const BANNER_BG = "#005448";
export const REMIND_DAYS = 28;
export const uid = () => Math.random().toString(36).slice(2, 12);
export const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
export const overlaps = (a, b) => a.x < b.x + b.w - 0.01 && b.x < a.x + a.w - 0.01 && a.y < b.y + b.h - 0.01 && b.y < a.y + a.h - 0.01;
export const deDate = (d) => (d ? d.split("-").reverse().join(".") : "");
export const ratioOf = (s) => (s?.width > 0 && s?.height > 0 ? s.width / s.height : 5);

function freeIntervals(blocks) {
  let iv = [[2, 98]];
  for (const b of blocks) {
    iv = iv.flatMap(([s, e]) => (b.x >= e || b.x + b.w <= s ? [[s, e]] : [[s, b.x], [b.x + b.w, e]].filter(([a, z]) => z - a > 3)));
  }
  return iv;
}

// Even row of n slots with generous gaps, skipping blocked zones; keeps ids/partners of existing slots (left to right).
export function distribute(n, blocks, ratio, existing = []) {
  const gap = 0.5;
  const iv = freeIntervals(blocks);
  if (!n || !iv.length) return [];
  const k = iv.map(() => 0);
  const width = (L, c) => L / (c + (c + 1) * gap);
  for (let i = 0; i < n; i += 1) {
    let best = 0;
    iv.forEach(([s, e], j) => { if (width(e - s, k[j] + 1) > width(iv[best][1] - iv[best][0], k[best] + 1)) best = j; });
    k[best] += 1;
  }
  const h = 62;
  let w = Math.min(...iv.map(([s, e], j) => (k[j] ? width(e - s, k[j]) : Infinity)));
  w = Math.min(w, (2.4 * h) / ratio);
  const old = [...existing].sort((a, b) => a.x - b.x);
  const out = [];
  iv.forEach(([s, e], j) => {
    const sp = (e - s - k[j] * w) / (k[j] + 1);
    for (let i = 0; i < k[j]; i += 1) {
      const prev = old[out.length];
      out.push({ id: prev?.id || uid(), customer_id: prev?.customer_id || null, x: s + sp + i * (w + sp), y: (100 - h) / 2, w, h });
    }
  });
  return out;
}

// Equal gaps only: keeps every slot's size and height position, order left to right, skips blocked zones.
export function respace(slots, blocks) {
  const iv = freeIntervals(blocks);
  const order = [...slots].sort((a, b) => a.x - b.x);
  const free = iv.reduce((t, [s, e]) => t + e - s, 0);
  const g = Math.max(0.5, (free - order.reduce((t, s) => t + s.w, 0)) / (order.length + iv.length));
  const groups = iv.map(() => []);
  let j = 0;
  let cur = iv[0]?.[0] + g;
  for (const s of order) {
    while (j < iv.length && cur + s.w > iv[j][1] + 0.01) { j += 1; cur = iv[j] ? iv[j][0] + g : 0; }
    if (j >= iv.length) return null;
    groups[j].push(s);
    cur += s.w + g;
  }
  return groups.flatMap((grp, i) => {
    const [s0, e0] = iv[i];
    const gap = (e0 - s0 - grp.reduce((t, s) => t + s.w, 0)) / (grp.length + 1);
    let x = s0 + gap;
    return grp.map((s) => { const out = { ...s, x, y: (100 - s.h) / 2 }; x += s.w + gap; return out; });
  });
}

export function freeSpot(slots, blocks, ratio) {
  const ref = slots.length ? slots.reduce((a, b) => (a.w * a.h <= b.w * b.h ? a : b)) : distribute(1, [], ratio)[0];
  for (let f = 1; f >= 0.4; f -= 0.2) {
    const w = ref.w * f;
    const h = ref.h * f;
    for (let y = (100 - h) / 2, x = 0.5; x <= 99.5 - w; x += 0.5) {
      const box = { x, y, w, h };
      if (![...slots, ...blocks].some((o) => overlaps(box, o))) return { ...box, id: uid(), customer_id: null };
    }
  }
  return null;
}

const addDays = (d, n) => { const x = new Date(`${d}T12:00:00Z`); x.setUTCDate(x.getUTCDate() + n); return x.toISOString().slice(0, 10); };

export function termStatus(p, today) {
  if (!p) return null;
  if (p.until && p.until < today) return { key: "expired", label: `abgelaufen ${deDate(p.until)}` };
  if (p.from && p.from > today) return { key: "planned", label: `ab ${deDate(p.from)}` };
  if (p.until && p.until <= addDays(today, REMIND_DAYS)) return { key: "ending", label: `läuft aus ${deDate(p.until)}` };
  return null;
}

export const numbering = (slots) => Object.fromEntries([...slots].sort((a, b) => a.x - b.x || a.y - b.y).map((s, i) => [s.id, i + 1]));

export const SILVER = "#A1AAAF";
// Display source: a ready-coloured print file as is, otherwise the logo silhouette tinted silver grey (as printed).
export function logoSource(p) {
  if (!p) return null;
  if (p.print?.preview_id) return { id: p.print.id, tint: !p.print.colored };
  if (p.logo?.preview_id) return { id: p.logo.id, tint: true };
  return null;
}

const cache = new Map();
const authPath = (gid) => `/partner-graphics/item/${gid}/preview`;
export function usePreview(gid, pathFn = authPath) {
  const path = gid ? pathFn(gid) : null;
  const [src, setSrc] = useState(path ? cache.get(path) || null : null);
  useEffect(() => {
    if (!path) { setSrc(null); return undefined; }
    if (cache.has(path)) { setSrc(cache.get(path)); return undefined; }
    let live = true;
    api.get(path, { responseType: "blob" })
      .then((r) => { const u = URL.createObjectURL(r.data); cache.set(path, u); if (live) setSrc(u); }).catch(() => {});
    return () => { live = false; };
  }, [path]);
  return src;
}
